import type { ComponentProps, MouseEvent } from "react"
import { useHrefWithParams, useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"

interface LegalLinkProps extends Omit<ComponentProps<"a">, "href"> {
  /** Target app route, e.g. `AppRoutes.PRIVACY`. */
  to: string
}

/**
 * Link to a legal page (privacy / terms / cookies) that navigates client-side via
 * the router instead of doing a full page reload, while keeping a real `href` (with
 * the current query params) for accessibility and open-in-new-tab. A plain `<a href>`
 * isn't intercepted by TanStack Router, so it reloads the whole SPA — a white flash
 * on click, and again when going Back.
 */
export function LegalLink({ to, onClick, children, ...props }: LegalLinkProps) {
  const buildHref = useHrefWithParams()
  const navigate = useNavigateWithParams()

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    onClick?.(e)
    if (e.defaultPrevented) return
    // Let the browser handle new-tab / modified clicks natively.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    navigate({ to })
  }

  return (
    <a data-slot="legal-link" href={buildHref(to)} onClick={handleClick} {...props}>
      {children}
    </a>
  )
}
